import { Controller } from "@hotwired/stimulus"
import { checkInStopFrom, checkInStopAfter, focusCheckInStop } from "./check_in_stops"

// One instance per client card's notice question on the Monday check-in
// (owner register 2026-10-09). It posts the answer and swaps the whole block
// for the server's redraw, so every sentence stays in the partial. The
// server is the authority: it refuses an answer laid over someone else's
// (the post carries what this card was showing) and sends back what is
// actually saved.
//
// Keyboard: the first choice is the card's last Tab stop. From there the
// choices answer by letter (N / Y, or S / A / G on a follow-up); "new"
// only OPENS the report step, which always takes a deliberate click.
// Enter never answers: the check-in's reflex is type, Enter, Tab, and Enter
// typed here by habit shows the hint instead of saving a "No notice" nobody
// looked for. A NUMBER typed here is the next row's number: it is carried
// to the next row's box (the question stays unanswered and the strip at the
// foot of the page says so), because eating it would put every number
// after it one row off.
export default class extends Controller {
  static values = { url: String, seen: String, hint: String, reportHint: String }
  static targets = ["ask", "report", "message", "songs"]

  change() {
    this.changing = true
    this.askTarget.style.display = ""
    this.firstChoice()?.focus()
  }

  choose(event) {
    const answer = event.currentTarget.dataset.answer
    if (answer === "new") this.openReport()
    else this.post({ answer })
  }

  // A box that opens with songs in it (Change, or a follow-up's last
  // report) ends them with ", " and puts the cursor after it, so a title
  // typed next never runs into the last one. The server trims an unused
  // ", ".
  openReport() {
    this.reportTarget.style.display = ""
    const box = this.songsTarget
    box.focus()
    if (box.value.trim()) {
      box.value = box.value.replace(/[,\s]*$/, ", ")
      box.setSelectionRange(box.value.length, box.value.length)
    }
  }

  cancelReport() {
    this.message("", true)
    this.reportTarget.style.display = "none"
    this.firstChoice()?.focus()
  }

  // The songs are one typed box (owner 2026-10-20: a notice can name a
  // song TREND is not promoting, so there is no pick list).
  report() {
    this.post({ answer: "new", songs: this.songsTarget.value })
  }

  onKeydown(event) {
    const el = event.target
    if (event.key === "Tab" && el.classList.contains("check-in-notice-stop")) {
      const next = checkInStopFrom(el, event.shiftKey)
      if (next) { event.preventDefault(); focusCheckInStop(next) }
      return
    }
    if (event.metaKey || event.ctrlKey || event.altKey) return
    if (!this.hasAskTarget || this.askTarget.style.display === "none") return
    // In the report step Escape is the way out, and Enter never sends or
    // answers: in the songs box, or back on a choice, it says what to do.
    if (this.reportTarget.style.display !== "none") {
      if (event.key === "Escape") { event.preventDefault(); this.cancelReport() }
      else if (event.key === "Enter" && (el === this.songsTarget || el.matches("button[data-answer]"))) {
        event.preventDefault()
        this.message(this.reportHintValue)
      }
      return
    }
    const key = event.key.toLowerCase()
    const button = Array.from(this.askTarget.querySelectorAll("button[data-key]")).find(b => b.dataset.key === key)
    if (button) { event.preventDefault(); this.message("", true); button.click(); return }
    // On a choice, Enter and numbers are the row reflex, not an answer.
    if (!el.matches("button[data-answer]")) return
    if (/^[0-9]$/.test(event.key)) {
      event.preventDefault()
      const next = checkInStopAfter(this.element)
      if (next && next.matches("input")) {
        next.focus()
        next.value = event.key
        next.dispatchEvent(new Event("input", { bubbles: true }))
      } else {
        this.message(this.hintValue)
      }
    } else if (event.key === "Enter") {
      event.preventDefault()
      this.message(this.hintValue)
    }
  }

  firstChoice() { return this.hasAskTarget ? this.askTarget.querySelector("button[data-answer]") : null }

  post(body) {
    if (this.busy) return
    this.busy = true
    const next = checkInStopAfter(this.element)
    fetch(this.urlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": this.csrf() },
      body: JSON.stringify({ ...body, seen: this.seenValue, change: this.changing ? "1" : "" })
    })
      .then(r => (r.ok ? r.json() : Promise.reject(r)))
      .then(data => this.redraw(data, next))
      .catch(() => { this.busy = false; this.message("Could not save. Check your connection and try again.") })
  }

  // Swap in what the server saved. A refusal redraws too (the card may be
  // showing a stale answer) and carries its sentence on the fresh block.
  redraw(data, next) {
    this.updateTally(data)
    if (!data || !data.html) {
      this.busy = false
      this.message((data && data.error) || "Could not save. Try again.")
      return
    }
    const holder = document.createElement("div")
    holder.innerHTML = data.html.trim()
    const fresh = holder.firstElementChild
    if (data.error) {
      const note = fresh.querySelector('[data-check-in-notice-target="message"]')
      if (note) { note.textContent = data.error; note.style.display = "block" }
    }
    this.element.replaceWith(fresh)
    // A refusal keeps the person on this card (and on the Tab path) to read
    // what was saved; an answer moves on.
    if (data.error || !next) fresh.querySelector("button")?.focus()
    else focusCheckInStop(next)
  }

  updateTally(data) {
    if (!data) return
    const set = (id, value) => {
      const el = document.getElementById(id)
      if (el && value !== undefined) el.textContent = value
    }
    set("notice-progress", data.answered)
    set("notice-total", data.total)
    const strip = document.getElementById("notice-strip")
    if (strip && data.strip_html) {
      const holder = document.createElement("div")
      holder.innerHTML = data.strip_html.trim()
      strip.replaceWith(holder.firstElementChild)
    }
  }

  message(text, hide = false) {
    if (!this.hasMessageTarget) return
    this.messageTarget.textContent = text
    this.messageTarget.style.display = hide || !text ? "none" : "block"
  }

  csrf() {
    const m = document.querySelector('meta[name="csrf-token"]')
    return m ? m.getAttribute("content") : ""
  }
}
